import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router'
import { useSelector } from 'react-redux'

import { DocumentMeta } from '../components/DocumentMeta'
import { loadCart } from '../store/actions'
import { track } from '../utils/tracking'
import { trackCartProductClick } from '../utils/googleAnalytics'
import { useTranslate } from '../utils/translate'
import Theme from '../components/Theme'
import useEffectExceptMount from '../utils/hooks/useEffectExceptMount'
import usePrevious from '../utils/hooks/usePrevious'

export default function Cart() {
  const t = useTranslate('shop', 'views.storefrontView.basketPageSection')

  const cart = useSelector<State, ImmutableMap>((state) => state.get('cart'))
  const shop = useSelector<State, ImmutableMap>((state) => state.get('shop'))
  const displayWishlistError = useSearchParams()[0]?.get('displayWishlistError')

  const [invalidProducts, setInvalidProducts] = useState(
    cart.get('productLineItems').filter((item) => item.get('productId') === null),
  )
  const previousCart = usePrevious(cart)

  const shopHasPickupAndShipping = Boolean(
    cart.getIn(['_links', 'shipping-method-available']) && cart.getIn(['_links', 'pickup-option-available']),
  )
  const cartHasShippingCosts = Boolean(cart.getIn(['shippingLineItem', 'lineItemPrice']))
  const showShippingCostEstimate = !shop.get('beyond') && cartHasShippingCosts

  useEffect(() => {
    setInvalidProducts(cart.get('productLineItems').filter((item) => item.get('productId') === null))
  }, [cart])

  useEffectExceptMount(() => {
    if (!previousCart?.equals(cart)) {
      track('cart:update', { cart: cart.toJS() })
    }
  }, [previousCart, cart])

  useEffect(() => {
    // Track unless the cart is empty
    if (cart.get('productLineItems')?.size > 0) {
      track('cart:view', { cart: cart.toJS(), shopLocale: shop.get('locale') })
    }
    // It should only trigger on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <DocumentMeta
        title={`${t('title')} - ${shop.get('title')}`}
        meta={[{ name: 'robots', content: 'noindex, follow' }]}
      />
      <Theme withLayout currentView="Cart">
        {(renderView, props) =>
          renderView('Cart', {
            ...props,
            cart: cart.toJS(),
            trackProductClick: trackCartProductClick,
            displayWishlistError,
            invalidProducts,
            shopHasPickupAndShipping,
            cartHasShippingCosts,
            showShippingCostEstimate,
          })
        }
      </Theme>
    </>
  )
}

Cart.storeUpdate = (props: RouteComponentProps, state: State) =>
  props.location.action === 'PUSH' || !state.get('cart') ? [loadCart()] : []

Cart.loadableSsrChunks = ['Cart']
Cart.contentCreationDisabled = () => true
